import { ChangeDetectionStrategy, Component, computed, DestroyRef, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { getAnimationAppearDisappear } from '@examdojo/animation';
import { ButtonComponent } from '@examdojo/core/button';
import { IconComponent } from '@examdojo/core/icon';
import { ImagePreviewDirective } from '@examdojo/ui/image-preview';
import { finalize, tap } from 'rxjs';
import { AgentRemoteImageChatMessageUIModel } from '../../models';
import { ImageChatCodeGenerationService } from '../../services';

@Component({
  selector: 'dojo-image-chat-agent-remote-message',
  standalone: true,
  template: `
    <div class="flex">
      @if (message().metadata && message().metadata?.contains_image_code) {
        <div class="flex flex-col gap-2 px-3 py-2.5">
          @if (imageFromCodeLoading()) {
            <div class="h-[150px] w-[250px] animate-pulse rounded-md bg-neutral-50"></div>
          } @else if (imageFromCode()) {
            <img [src]="imageFromCodeBlobUrl()" @appearDisappear dojoImagePreview alt="image" class="h-[150px]" />
          } @else {
            <span>No image</span>
          }

          <dojo-button
            class="relative top-[-2px] flex"
            [size]="'s'"
            [fill]="'outline'"
            [customWidth]="120"
            [customColor]="'sky'"
            [pending]="imageFromCodeApplying()"
            (click)="applyCode()"
          >
            <dojo-icon size="14" icon="arrow-down"></dojo-icon>
            <span class="ml-1">Apply code</span>
          </dojo-button>
        </div>
      } @else {
        <div class="flex items-center justify-center gap-2 p-4">
          <span>No code for image</span>
        </div>
      }
    </div>
  `,
  imports: [MatTooltipModule, ImagePreviewDirective, ButtonComponent, IconComponent],
  animations: [getAnimationAppearDisappear()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageChatAgentRemoteMessageComponent implements OnInit {
  constructor(
    private readonly codeGenerationService: ImageChatCodeGenerationService,
    private readonly destroyRef: DestroyRef,
  ) {}

  readonly message = input.required<AgentRemoteImageChatMessageUIModel>();

  readonly imageFromCode = signal<File | null>(null);
  readonly imageFromCodeLoading = signal(false);
  readonly imageFromCodeApplying = signal(false);
  readonly imageFromCodeBlobUrl = computed(() => {
    const image = this.imageFromCode();
    return image ? URL.createObjectURL(image) : '';
  });

  ngOnInit(): void {
    this.generateCode();
  }

  applyCode() {
    this.imageFromCodeApplying.set(true);
    this.codeGenerationService
      .applyCodeAndImage(this.imageFromCode(), this.message().message.text)
      .pipe(
        finalize(() => {
          this.imageFromCodeApplying.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  generateCode() {
    if (this.imageFromCode() || this.imageFromCodeLoading()) {
      return;
    }

    this.imageFromCodeLoading.set(true);
    this.codeGenerationService
      .generateCode(this.message().message.text)
      .pipe(
        tap((file) => {
          this.imageFromCode.set(file);
        }),
        finalize(() => {
          this.imageFromCodeLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
